export const USER_ROLES = {
  ADMIN: 'admin',
  INDIVIDUAL_CONTRACTOR: 'individual_contractor',
  COMPANY_CONTRACTOR: 'company_contractor',
  GIG_WORKER: 'gig_worker',
  EMPLOYEE: 'employee',
  EMPLOYER: 'employer',
  EMPLOYMENT_MANAGER: 'employment_manager',
  FINANCE: 'finance',
  INVOICE_MANAGER: 'invoice_manager',
  INVOICE_USER: 'invoice_user',
  LEGAL: 'legal',
  ONBOARDING_MANAGER: 'onboarding_manager',
  RELATIONSHIP_MANAGER: 'relationship_manager',
  KEY_ACCOUNT_MANAGER: 'key_account_manager',
  ORGANISATION_OWNER: 'organisation_owner',
  READER: 'reader',
  CX_MANAGER: 'cx_manager',
  PAYROLL_SEAT: 'payroll_seat',
  DRAFT_SEAT: 'draft_seat',
  TEAMS_GLOBAL_ADMIN: 'teams_global_admin',
  GLOBAL_ADMIN: 'teams_global_admin',
  SALES_MANAGER: 'sales_manager',
  PAYROLL_MANAGER: 'payroll_manager',
  INVOICING_MANAGER: 'invoicing_manager',
};

export const CONTRACTOR_ROLES = {
  INDIVIDUAL_CONTRACTOR: 'individual_contractor',
  COMPANY_CONTRACTOR: 'company_contractor',
} as const;

export const PERMISSIONS = {
  // Create permissions
  CREATE_ANY_CONTRACTORS: 'create any contractors',
  CREATE_ANY_EMPLOYEES: 'create any employees',
  CREATE_ANY_EMPLOYMENT_DETAILS: 'create any employment_details',
  CREATE_ANY_MSA_DETAILS: 'create any msa_details',
  CREATE_ANY_ORGANISATIONS: 'create any organisations',
  CREATE_ANY_USERS: 'create any users',
  CREATE_OWN_CONTRACTORS: 'create own contractors',
  CREATE_OWN_EMPLOYEES: 'create own employees',
  CREATE_OWN_EMPLOYMENT_DETAILS: 'create own employment_details',
  CREATE_OWN_MSA_DETAILS: 'create own msa_details',
  CREATE_OWN_USERS: 'create own users',

  // Delete permissions
  DELETE_ANY_CONTRACTORS: 'delete any contractors',
  DELETE_ANY_EMPLOYEES: 'delete any employees',
  DELETE_ANY_EMPLOYMENT_DETAILS: 'delete any employment_details',
  DELETE_ANY_MSA_DETAILS: 'delete any msa_details',
  DELETE_ANY_ORGANISATIONS: 'delete any organisations',
  DELETE_ANY_USERS: 'delete any users',
  DELETE_OWN_CONTRACTORS: 'delete own contractors',
  DELETE_OWN_EMPLOYEES: 'delete own employees',
  DELETE_OWN_EMPLOYMENT_DETAILS: 'delete own employment_details',
  DELETE_OWN_MSA_DETAILS: 'delete own msa_details',
  DELETE_OWN_ORGANISATIONS: 'delete own organisations',
  DELETE_OWN_USERS: 'delete own users',

  // Update permissions
  UPDATE_ANY_CONTRACTORS: 'update any contractors',
  UPDATE_ANY_EMPLOYEES: 'update any employees',
  UPDATE_ANY_EMPLOYMENT_DETAILS: 'update any employment_details',
  UPDATE_ANY_MSA_DETAILS: 'update any msa_details',
  UPDATE_ANY_ORGANISATIONS: 'update any organisations',
  UPDATE_ANY_USERS: 'update any users',
  UPDATE_OWN_CONTRACTORS: 'update own contractors',
  UPDATE_OWN_EMPLOYEES: 'update own employees',
  UPDATE_OWN_EMPLOYMENT_DETAILS: 'update own employment_details',
  UPDATE_OWN_MSA_DETAILS: 'update own msa_details',
  UPDATE_OWN_ORGANISATIONS: 'update own organisations',
  UPDATE_OWN_USERS: 'update own users',
  UPDATE_SELF_CONTRACTORS: 'update self contractors',
  UPDATE_SELF_EMPLOYEES: 'update self employees',
  UPDATE_SELF_USERS: 'update self users',

  // View permissions
  VIEW_ANY_CONTRACTORS: 'view any contractors',
  VIEW_ANY_EMPLOYEES: 'view any employees',
  VIEW_ANY_EMPLOYMENT_DETAILS: 'view any employment_details',
  VIEW_ANY_MSA_DETAILS: 'view any msa_details',
  VIEW_ANY_ORGANISATIONS: 'view any organisations',
  VIEW_ANY_USERS: 'view any users',
  VIEW_OWN_CONTRACTORS: 'view own contractors',
  VIEW_OWN_EMPLOYEES: 'view own employees',
  VIEW_OWN_EMPLOYMENT_DETAILS: 'view own employment_details',
  VIEW_OWN_MSA_DETAILS: 'view own msa_details',
  VIEW_OWN_ORGANISATIONS: 'view own organisations',
  VIEW_OWN_USERS: 'view own users',
  VIEW_ANY_WALLETS: 'view any wallets',

  REVIEW_ANY_PAYROLLS: 'review any payrolls',
};

export const MSA_VERIFICATION_TYPES = {
  MSA_DETAILS: 'msa_details',
  EMPLOYMENT_DETAILS: 'employment_details',
};

export const MSA_DETAILS_STATUS = {
  NOT_STARTED: 'not_started',
  UNDER_REVIEW: 'under_review',
  AWAITING_COMPANY_OWNER_APPROVAL: 'awaiting_the_company_owner',
  AWAITING_FINAL_SIGNATURE: 'awaiting_final_signature',
  SIGNED: 'signed',
};

export const KYB_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
};

export const KYC_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
};

export const CONTINENTS_TO_VALUE = {
  Europe: 1,
  Africa: 2,
  'North America': 3,
  'South America': 3,
  Asia: 4,
  Oceania: 5,
};

export const EMPLOYEE_TYPE = {
  LOCAL: 'local',
  EXPAT: 'expat',
};

export const EMPLOYEE_CONTRACT_TYPE = {
  FIXED: 'fixed',
  PERMANENT: 'indefinite',
};

// Backend-assigned employee job ids that the UI needs to branch on.
// Keep in sync with the `employee_jobs` table; the `OTHER` row drives the
// custom job title/description flow in the admin TMP.
export const EMPLOYEE_JOB_ID = {
  OTHER: 103,
} as const;

export const INVOICE_TYPES = {
  DEPOSIT: 'deposit',
  PAYROLL: 'payroll',
  GENERAL: 'general',
};

export const EMPLOYEE_STATUS = {
  EMPLOYED: 'employed',
  REJECTED: 'rejected',
  EMPLOYEE_REQUESTED_CHANGES: 'employee_requested_changes',
  AWAITING_FINAL_SIGNATURE: 'waiting_for_signature',
  UNDER_REVIEW: 'under_review',
  DRAFT: 'draft',
  TERMINATED: 'terminated',
  EMPLOYER_REQUESTED_CHANGES: 'employer_requested_changes',
};

export const PERSON_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  ONBOARDING: 'onboarding',
} as const;

export const ENTITY_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
} as const;

export const ENTITY_OWNERSHIP_TYPE = {
  NT_OWNED: 'nt_owned',
  CLIENT_OWNED: 'client_owned',
} as const;

export const USER_TYPE = {
  EMPLOYER: 'employer',
  EMPLOYEE: 'employee',
};

export const EMPLOYEE_PAYROLL_TYPES = {
  BONUS: 'bonus',
  EXPENSE: 'expense',
  SICK_LEAVE: 'sick_leave',
  CONTRACTED_ANNUAL_LEAVE: 'contracted_annual_leave',
  UNPAID_LEAVE: 'unpaid_leave',
  SALARY_CHANGE: 'salary_change',
  OTHER: 'other',
  OVERTIME_HOURS: 'overtime_hours',
  NONE: 'none',
  CONTRACT_AMENDMENT: 'contract_amendment',
};

export const EMPLOYEE_PAYROLL_EXPENSE_TYPES = {
  PREPAID_EXPENSE: 'prepaid_expense',
  REIMBURSEMENT: 'reimbursement',
};

export const PAYROLL_STATUS = {
  ALL: 'all',
  PENDING: 'pending',
  APPROVED: 'approved',
  DECLINED: 'declined',
};

export const PAYROLL_ALERT_TYPE = {
  ADD: 'add',
  EDIT: 'edit',
};

export const DOCUMENT_TYPES = {
  ANNEXE: 'annexe',
  PAYSLIP: 'payslip',
  CONTRACT: 'contract',
  MSA: 'msa',
  OTHER: 'other',
  PRICING_ADDENDUM: 'pricing_addendum',
};

export const INVOICE_STATUS = {
  PENDING: 'pending',
  PAID: 'paid',
  OVERDUE: 'overdue',
  DRAFT: 'draft',
  ALL: 'all',
};

export const PAYROLL_DIALOG_STATE = {
  ADD: 'add',
  EDIT: 'edit',
  MANAGE: 'manage',
  VIEW: 'view',
};
export const TOP_UP_METHOD_OPTIONS = {
  CARD: 'card',
  BANK: 'bank',
} as const;

export const BANK_TRANSFER_TYPE_OPTIONS = {
  SEPA: 'sepa',
  SWIFT: 'swift',
} as const;

export const BANK_ACCOUNT_TYPES = {
  IBAN: 'iban',
  ACCOUNT_NUMBER: 'account_number',
} as const;

export const ACCESSIBLE_APPS = {
  ADMIN: 'admin',
  EMPLOYEE: 'employee',
  CONTRACTOR: 'contractor',
  EMPLOYER: 'employer',
  WALLET: 'wallet',
  GIG_WORKER: 'gig_worker',
};

export const ALERT_PRIORITY = {
  MEDIUM: 'medium',
  HIGHEST: 'highest',
  CRITICAL: 'critical',
};

export const KYT_STATUS_OPTIONS = {
  SUCCESS: 'success',
  NOT_CHECKED: 'notChecked',
  HIGH_RISK: 'highRisk',
  FAIL: 'fail',
} as const;

export const PAYMENT_REQUEST_STATUS_OPTIONS = {
  NEW: 'new',
  REFUNDED: 'refunded',
  PENDING: 'pending',
  RESERVED: 'reserved',
  COMPLETED: 'completed',
  PAID: 'paid',
  DECLINED: 'declined',
  CANCELLED: 'cancelled',
  FAILED: 'failed',
} as const;

export const PAYMENT_REQUEST_UPDATE_STATUS_OPTIONS = {
  PENDING: 'pending',
  APPROVE: 'approve',
  PAY: 'pay',
  DECLINE: 'decline',
} as const;

export const PAYMENTS_TO_DO_LIST_OPTIONS = {
  WITHDRAWAL_REQUESTS: 'withdrawal-requests',
  EPS_CARD_TOP_UPS: 'eps-card-top-up-requests',
  EPS_CARD_REQUESTS: 'eps-card-requests',
  KYT_CHECKS: 'kyt-checks',
  WALLET_TOP_UP_REQUESTS: 'wallet-top-up-requests',
  USER_VERIFICATION_REQUESTS: 'user-verification-requests',
  CLIENT_INVOICING_REQUESTS: 'client-invoicing-requests',
  IBAN_REQUESTS: 'iban-requests',
  CONTRACTOR_PAYMENTS_REQUESTS: 'contractor-payments-requests',
} as const;

export const TRANSACTION_REQUEST_TYPES = {
  WITHDRAWAL: 'outbound',
  TOP_UP: 'inbound',
} as const;

export const TOP_UP_PROVIDER_OPTIONS = {
  BANK: 'bank',
  CARD: 'card',
} as const;

export const EPS_CARD_REQUEST_STATUS_OPTIONS = {
  NEW: 'new',
  PENDING: 'pending',
  ACCEPTED: 'accepted',
  SENT: 'sent',
  ACTIVE: 'active',
  DECLINED: 'declined',
  FAILED: 'failed',
} as const;

export const DYNAMIC_FIELD_TYPES = {
  SELECT: 'select',
  DATE: 'date',
  TEXT: 'text',
} as const;

export type DynamicFieldType = (typeof DYNAMIC_FIELD_TYPES)[keyof typeof DYNAMIC_FIELD_TYPES];

export const DYNAMIC_FIELD_CONDITIONAL_REQUIRED_CONDITIONS = {
  EQUALS: 'equals',
} as const;

export type DynamicConditionalRequiredCondition =
  (typeof DYNAMIC_FIELD_CONDITIONAL_REQUIRED_CONDITIONS)[keyof typeof DYNAMIC_FIELD_CONDITIONAL_REQUIRED_CONDITIONS];

export const DYNAMIC_FIELD_CONDITIONAL_DEPENDS_CONDITIONS = {
  LESS_THAN: 'less_than',
  EQUALS: 'equals',
} as const;

export type DynamicConditionalDependsCondition =
  (typeof DYNAMIC_FIELD_CONDITIONAL_DEPENDS_CONDITIONS)[keyof typeof DYNAMIC_FIELD_CONDITIONAL_DEPENDS_CONDITIONS];

export const DYNAMIC_FIELD_CALCULATION_TYPES = {
  ADD_YEARS_SUBTRACT_DAYS: 'add_years_subtract_days',
} as const;

export type DynamicCalculationType =
  (typeof DYNAMIC_FIELD_CALCULATION_TYPES)[keyof typeof DYNAMIC_FIELD_CALCULATION_TYPES];

export const API_RESPONSE_TYPES = {
  GENERAL_SUCCESS: 'general_success',
} as const;

export const USER_VERIFICATION_STATUS_OPTIONS = {
  NEW: 'initial',
  VERIFIED: 'approved',
  REJECTED: 'rejected',
  FINAL_REJECTED: 'final_rejected',
} as const;

export const USER_VERIFICATION_SUM_SUB_STATUS_OPTIONS = {
  NEW: 'init',
  PENDING: 'pending',
  ON_HOLD: 'on_hold',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  FINAL_REJECTED: 'final_rejected',
} as const;

export const WALLET_TRANSACTION_TYPE_OPTIONS = [
  { value: 'inbound', name: 'Inbound' },
  { value: 'outbound', name: 'Outbound' },
  { value: 'internal', name: 'Internal' },
];

export const WALLET_STATUS_OPTIONS = [
  { value: 'completed', name: 'Completed' },
  { value: 'pending', name: 'Pending' },
  { value: 'declined', name: 'Declined' },
  { value: 'refunded', name: 'Refunded' },
  { value: 'reserved', name: 'Reserved' },
  { value: 'cancelled', name: 'Cancelled' },
];

export const DEPOSITS_TRANSACTION_TYPE_OPTIONS = [
  { value: 'pre-payments', name: 'Deposits' },
  { value: 'rewards', name: 'Rewards' },
];

export const ACTION_TYPES = {
  APPROVED: 'approved',
  REJECTED: 'rejected',
  CONFIRM_REJECT: 'confirm_reject',
  CONFIRM_APPROVE: 'confirm_approve',
  // This is the status which the BE accepts for rejected status
  DECLINED: 'declined',
};

export const CTA_TYPES = {
  TEAM: 'team',
  INVOICES: 'invoices',
  PAYROLL: 'payroll',
  SETTINGS: 'settings',
  BANK_INFO: 'bank-info',
  ABSENCE: 'absence',
  TEAM_MEMBER_PROFILE: 'team_member_profile',
};

export const TASK_CATEGORIES = {
  COMPANY: 'company',
  TEAM_MEMBER: 'team_member',
  INVOICE: 'invoice',
};

export const TASK_TYPES = {
  CONTRACT_SIGNING: 'contract_signing',
  CONTRACT_CHANGES: 'contract_changes',
};

export const TASK_STATUSES = {
  PENDING: 'pending',
  COMPLETED: 'completed',
  OVERDUE: 'overdue',
};

export const DEFAULT_PAGE_INDEX = 1;
export const DEFAULT_PAGE_SIZE = 10;

export const KYT_CHECKS_FILTER_TYPE_OPTIONS = {
  CAUSER_IP: 'causer_ip',
  CHECKUP_ID: 'checkup_id',
  DATE: 'date',
  USER: 'user',
} as const;

export const TEAM_ROLE_IDS = {
  OWNER: 'employer',
  GLOBAL_ADMIN: 'teams_global_admin',
  TEAM_LEAD: 'teams_team_lead',
  TEAM_MEMBER: 'teams_team_member',
} as const;

export const EMPLOYMENT_TYPE_OPTIONS = {
  EMPLOYEE: 'employee',
  INDIVIDUAL_CONTRACTOR: 'individual_contractor',
  COMPANY_CONTRACTOR: 'company_contractor',
  GIG_WORKER: 'gig_worker',
  OTHER: 'other',
} as const;

export const ADMIN_EMPLOYMENT_TYPE_OPTIONS = {
  EMPLOYEE: 'employee',
  INDIVIDUAL_CONTRACTOR: 'individual_contractor',
  COMPANY_CONTRACTOR: 'company_contractor',
  GIG_WORKER: 'gig_worker',
} as const;

export const SALARY_TYPE_OPTIONS = {
  NET: 'net',
  GROSS: 'gross',
} as const;

export const TEAM_MEMBER_TYPE = {
  EMPLOYEE: 'employee',
  CONTRACTOR: 'contractor',
  GIG_WORKER: 'gig_worker',
  OTHER: 'other',
} as const;

export const TEAM_ROLES = {
  TEAM_LEAD: 'teams_team_lead',
  TEAM_MEMBER: 'teams_team_member',
};

export const ABSENCE_TYPES = {
  CONTRACTED_ANNUAL_LEAVE: 'contracted_annual_leave',
  SICK_LEAVE: 'sick_leave',
  UNPAID_LEAVE: 'unpaid_leave',
} as const;

export type AbsenceType = (typeof ABSENCE_TYPES)[keyof typeof ABSENCE_TYPES];

export const NOTIFICATION_CATEGORIES = {
  PAYROLL: 'payroll',
  INVOICES: 'invoices',
  SETTINGS: 'settings',
  TASK: 'task',
  ORGANISATION: 'organisation',
  TEAM_MEMBERS: 'team_members',
  TEAMS: 'teams',
  MSA: 'msa',
  KYB: 'kyb',
} as const;

export const WALLET_TYPES = {
  DEFAULT: 'default',
  PRE_PAYMENT: 'pre_payment',
  ORGANISATION: 'organisation',
  ENTITY: 'entity',
};
export const ENTITY_ACTIVITY_TYPES = {
  TEAM_MEMBERS: 'team_members',
  PAYROLL: 'payroll',
  BASIC: 'basic',
} as const;
